import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Upload, FileSpreadsheet, CheckCircle, AlertCircle,
  BookOpen, Receipt, Loader2, Info, Package, FolderOpen, MapPin, Columns, Users, Truck, Eye
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import axios from 'axios';
import FieldMapper from '../components/shared/FieldMapper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// System field definitions per import type
const COA_FIELDS = [
  { key: 'account_code', label: 'Account Code', required: true, defaultCol: 0, keywords: ['كود الحساب', 'code', 'كود'] },
  { key: 'account_name', label: 'Account Name', required: true, defaultCol: 2, keywords: ['اسم الحساب', 'name', 'اسم'] },
  { key: 'account_type', label: 'Type', required: false, defaultCol: 3, keywords: ['النوع', 'type'] },
  { key: 'contact_name', label: 'Contact Name', required: false, defaultCol: 13, keywords: ['NAME', 'contact'] },
  { key: 'address', label: 'Address', required: false, defaultCol: 14, keywords: ['ADDRESS', 'عنوان'] },
  { key: 'phone', label: 'Phone', required: false, defaultCol: 15, keywords: ['PHONE', 'تلفون', 'هاتف'] },
  { key: 'reg_id', label: 'Region ID', required: false, defaultCol: 18, keywords: ['REG_ID', 'region'] },
  { key: 'regno', label: 'Registration No', required: false, defaultCol: 27, keywords: ['REGNO', 'registration', 'ض.ق.م'] },
];

const VOUCHER_FIELDS = [
  { key: 'tran', label: 'Transaction ID', required: true, defaultCol: 0, keywords: ['TRAN', 'transaction'] },
  { key: 'account_code', label: 'Account Code', required: true, defaultCol: 3, keywords: ['كود', 'code', 'account'] },
  { key: 'date', label: 'Date', required: true, defaultCol: 5, keywords: ['DATE', 'تاريخ'] },
  { key: 'cr_lbp', label: 'Credit LBP', required: true, defaultCol: 8, keywords: ['TOTCL', 'credit.*lbp'] },
  { key: 'dr_lbp', label: 'Debit LBP', required: true, defaultCol: 10, keywords: ['TOTDL', 'debit.*lbp'] },
  { key: 'cr_usd', label: 'Credit USD', required: false, defaultCol: 11, keywords: ['TOTCU', 'credit.*usd'] },
  { key: 'dr_usd', label: 'Debit USD', required: false, defaultCol: 12, keywords: ['TOTDU', 'debit.*usd'] },
  { key: 'description', label: 'Description', required: false, defaultCol: 14, keywords: ['الوصف', 'desc'] },
  { key: 'currency', label: 'Currency (1=LBP,2=USD)', required: false, defaultCol: 17, keywords: ['CUR', 'currency'] },
];

const INVENTORY_FIELDS = [
  { key: 'item_code', label: 'Item Code', required: true, defaultCol: 0, keywords: ['كود', 'code', 'item'] },
  { key: 'name', label: 'Item Name (Arabic)', required: true, defaultCol: 2, keywords: ['ADESC', 'اسم', 'name', 'arabic'] },
  { key: 'description', label: 'Description', required: false, defaultCol: 1, keywords: ['الوصف', 'desc'] },
  { key: 'category_id', label: 'Category ID', required: false, defaultCol: 3, keywords: ['CAT_ID', 'category'] },
  { key: 'supplier_id', label: 'Supplier Code', required: false, defaultCol: 4, keywords: ['SUP_ID', 'supplier'] },
  { key: 'package', label: 'Package (numeric)', required: false, defaultCol: 5, keywords: ['PAK', 'package', 'pack'] },
  { key: 'pack_desc', label: 'Pack Description', required: false, defaultCol: 6, keywords: ['PACK'] },
  { key: 'price', label: 'Selling Price', required: false, defaultCol: 7, keywords: ['PRICE', 'سعر', 'sell'] },
  { key: 'cost', label: 'Cost Price', required: false, defaultCol: 8, keywords: ['COST', 'تكلفة'] },
  { key: 'tva', label: 'TVA (11=Taxed)', required: false, defaultCol: 22, keywords: ['TVA', 'tax', 'ضريبة'] },
];

// Customer/Supplier field definitions
const CUSTOMER_SUPPLIER_FIELDS = [
  { key: 'name', label: 'Name', required: true, defaultCol: 0, keywords: ['name', 'اسم'] },
  { key: 'address', label: 'Address', required: false, defaultCol: 1, keywords: ['address', 'عنوان'] },
  { key: 'currency', label: 'Currency (D=USD, L=LBP)', required: false, defaultCol: 2, keywords: ['cur', 'currency', 'عملة'] },
  { key: 'balance', label: 'Balance', required: false, defaultCol: 3, keywords: ['balance', 'رصيد'] },
  { key: 'phone', label: 'Phone', required: false, defaultCol: 4, keywords: ['phone', 'هاتف', 'تلفون'] },
  { key: 'comments', label: 'Comments/Notes', required: false, defaultCol: 5, keywords: ['comment', 'note', 'ملاحظات'] },
];

// Result Display
const ResultDisplay = ({ result }) => {
  if (!result) return null;
  return (
    <div className={`p-3 rounded-lg border text-xs ${result.error ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`}>
      {result.error ? (
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
          <p className="text-red-600">{result.message}</p>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
          <div className="text-muted-foreground">
            {Object.entries(result).filter(([k]) => k !== 'message' && k !== 'errors' && k !== 'error_count' && k !== 'success').map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4">
                <span>{k.replace(/_/g, ' ')}:</span>
                <span className="font-mono font-medium">{typeof v === 'number' ? v.toLocaleString() : String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// File Upload
const FileUploader = ({ file, setFile, color, onClear }) => (
  <label className={`flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
    file ? 'border-blue-400 bg-blue-50/50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'
  }`}>
    <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => { setFile(e.target.files[0]); if(onClear) onClear(); }} />
    {file ? (
      <div className="text-center">
        <FileSpreadsheet className="w-6 h-6 mx-auto text-blue-500 mb-1" />
        <p className="text-xs font-medium truncate max-w-[200px]">{file.name}</p>
        <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
      </div>
    ) : (
      <div className="text-center">
        <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-1" />
        <p className="text-xs text-muted-foreground">Click to select file (.xlsx)</p>
      </div>
    )}
  </label>
);

const ImportDataPage = () => {
  const { currentOrg } = useAuth();
  const { fiscalYears, selectedFY } = useFiscalYear();
  
  // Import states
  const [coaFile, setCoaFile] = useState(null);
  const [coaResult, setCoaResult] = useState(null);
  const [coaImporting, setCoaImporting] = useState(false);
  
  const [voucherFile, setVoucherFile] = useState(null);
  const [voucherResult, setVoucherResult] = useState(null);
  const [voucherImporting, setVoucherImporting] = useState(false);
  const [voucherFYId, setVoucherFYId] = useState(selectedFY?.id || '');
  
  const [catFile, setCatFile] = useState(null);
  const [catResult, setCatResult] = useState(null);
  const [catImporting, setCatImporting] = useState(false);
  
  const [regionFile, setRegionFile] = useState(null);
  const [regionResult, setRegionResult] = useState(null);
  const [regionImporting, setRegionImporting] = useState(false);
  
  const [itemsFile, setItemsFile] = useState(null);
  const [itemsResult, setItemsResult] = useState(null);
  const [itemsImporting, setItemsImporting] = useState(false);

  // Customer/Supplier import states
  const [customerFile, setCustomerFile] = useState(null);
  const [customerPreview, setCustomerPreview] = useState(null);
  const [customerPreviewLoading, setCustomerPreviewLoading] = useState(false);
  const [customerImporting, setCustomerImporting] = useState(false);
  const [customerResult, setCustomerResult] = useState(null);
  const [customerPreviewOpen, setCustomerPreviewOpen] = useState(false);
  const [customerFieldMapping, setCustomerFieldMapping] = useState(null);
  const [customerMapperOpen, setCustomerMapperOpen] = useState(false);
  const [customerMapperHeaders, setCustomerMapperHeaders] = useState([]);
  const [customerMapperSamples, setCustomerMapperSamples] = useState([]);
  
  const [supplierFile, setSupplierFile] = useState(null);
  const [supplierPreview, setSupplierPreview] = useState(null);
  const [supplierPreviewLoading, setSupplierPreviewLoading] = useState(false);
  const [supplierImporting, setSupplierImporting] = useState(false);
  const [supplierResult, setSupplierResult] = useState(null);
  const [supplierPreviewOpen, setSupplierPreviewOpen] = useState(false);
  const [supplierFieldMapping, setSupplierFieldMapping] = useState(null);
  const [supplierMapperOpen, setSupplierMapperOpen] = useState(false);
  const [supplierMapperHeaders, setSupplierMapperHeaders] = useState([]);
  const [supplierMapperSamples, setSupplierMapperSamples] = useState([]);

  // Field mapping state
  const [mapperOpen, setMapperOpen] = useState(false);
  const [mapperType, setMapperType] = useState(null); // 'coa', 'voucher', 'inventory'
  const [mapperHeaders, setMapperHeaders] = useState([]);
  const [mapperSamples, setMapperSamples] = useState([]);
  const [mapperFile, setMapperFile] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Simple import (no mapping needed)
  const doSimpleImport = async (endpoint, file, setImporting, setResult, extraData = {}) => {
    if (!file || !currentOrg) return;
    setImporting(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('organization_id', currentOrg.id);
      Object.entries(extraData).forEach(([k, v]) => { if(v) formData.append(k, v); });
      const response = await axios.post(`${API}${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000
      });
      setResult(response.data);
    } catch (err) {
      setResult({ error: true, message: err.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  // Start field mapping flow: preview headers then show mapper
  const startMapping = async (type, file) => {
    if (!file) return;
    setPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/import/preview-headers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMapperHeaders(response.data.headers);
      setMapperSamples(response.data.sample_rows);
      setMapperType(type);
      setMapperFile(file);
      setMapperOpen(true);
    } catch (err) {
      alert('Failed to read file headers: ' + (err.response?.data?.detail || err.message));
    } finally {
      setPreviewLoading(false);
    }
  };

  // Execute import with mapping
  const doMappedImport = async (mapping) => {
    setMapperOpen(false);
    const mappingJson = JSON.stringify(mapping);
    
    if (mapperType === 'coa') {
      setCoaImporting(true);
      setCoaResult(null);
      try {
        const formData = new FormData();
        formData.append('file', mapperFile);
        formData.append('organization_id', currentOrg.id);
        formData.append('field_mapping', mappingJson);
        const r = await axios.post(`${API}/import/chart-of-accounts`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000
        });
        setCoaResult(r.data);
      } catch (err) {
        setCoaResult({ error: true, message: err.response?.data?.detail || 'Import failed' });
      } finally {
        setCoaImporting(false);
      }
    } else if (mapperType === 'voucher') {
      setVoucherImporting(true);
      setVoucherResult(null);
      try {
        const formData = new FormData();
        formData.append('file', mapperFile);
        formData.append('organization_id', currentOrg.id);
        formData.append('field_mapping', mappingJson);
        if (voucherFYId) formData.append('fiscal_year_id', voucherFYId);
        const r = await axios.post(`${API}/import/vouchers`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000
        });
        setVoucherResult(r.data);
      } catch (err) {
        setVoucherResult({ error: true, message: err.response?.data?.detail || 'Import failed' });
      } finally {
        setVoucherImporting(false);
      }
    } else if (mapperType === 'inventory') {
      setItemsImporting(true);
      setItemsResult(null);
      try {
        const formData = new FormData();
        formData.append('file', mapperFile);
        formData.append('organization_id', currentOrg.id);
        formData.append('field_mapping', mappingJson);
        const r = await axios.post(`${API}/import/inventory`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000
        });
        setItemsResult(r.data);
      } catch (err) {
        setItemsResult({ error: true, message: err.response?.data?.detail || 'Import failed' });
      } finally {
        setItemsImporting(false);
      }
    }
  };

  // Customer preview
  const previewCustomers = async (withMapping = null) => {
    if (!customerFile || !currentOrg) return;
    setCustomerPreviewLoading(true);
    setCustomerResult(null);
    try {
      const formData = new FormData();
      formData.append('file', customerFile);
      formData.append('organization_id', currentOrg.id);
      if (withMapping) {
        formData.append('field_mapping', JSON.stringify(withMapping));
      }
      const response = await axios.post(`${API}/import/preview-customers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000
      });
      setCustomerPreview(response.data);
      setCustomerFieldMapping(withMapping || response.data.column_mapping);
      setCustomerPreviewOpen(true);
    } catch (err) {
      setCustomerResult({ error: true, message: err.response?.data?.detail || 'Preview failed' });
    } finally {
      setCustomerPreviewLoading(false);
    }
  };

  // Open customer field mapper
  const openCustomerFieldMapper = async () => {
    if (!customerFile) return;
    setCustomerPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', customerFile);
      const response = await axios.post(`${API}/import/preview-customer-headers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCustomerMapperHeaders(response.data.headers);
      setCustomerMapperSamples(response.data.sample_rows);
      setCustomerMapperOpen(true);
    } catch (err) {
      setCustomerResult({ error: true, message: err.response?.data?.detail || 'Failed to read file headers' });
    } finally {
      setCustomerPreviewLoading(false);
    }
  };

  // Handle customer field mapping confirmation
  const handleCustomerMappingConfirm = (mapping) => {
    setCustomerMapperOpen(false);
    setCustomerFieldMapping(mapping);
    previewCustomers(mapping);
  };

  // Import customers
  const importCustomers = async () => {
    if (!customerPreview || !currentOrg) return;
    setCustomerImporting(true);
    setCustomerPreviewOpen(false);
    try {
      const formData = new FormData();
      formData.append('organization_id', currentOrg.id);
      formData.append('records', JSON.stringify(customerPreview.all_records));
      const response = await axios.post(`${API}/import/customers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000
      });
      setCustomerResult(response.data);
      setCustomerPreview(null);
    } catch (err) {
      setCustomerResult({ error: true, message: err.response?.data?.detail || 'Import failed' });
    } finally {
      setCustomerImporting(false);
    }
  };

  // Supplier preview
  const previewSuppliers = async (withMapping = null) => {
    if (!supplierFile || !currentOrg) return;
    setSupplierPreviewLoading(true);
    setSupplierResult(null);
    try {
      const formData = new FormData();
      formData.append('file', supplierFile);
      formData.append('organization_id', currentOrg.id);
      if (withMapping) {
        formData.append('field_mapping', JSON.stringify(withMapping));
      }
      const response = await axios.post(`${API}/import/preview-suppliers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000
      });
      setSupplierPreview(response.data);
      setSupplierFieldMapping(withMapping || response.data.column_mapping);
      setSupplierPreviewOpen(true);
    } catch (err) {
      setSupplierResult({ error: true, message: err.response?.data?.detail || 'Preview failed' });
    } finally {
      setSupplierPreviewLoading(false);
    }
  };

  // Open supplier field mapper
  const openSupplierFieldMapper = async () => {
    if (!supplierFile) return;
    setSupplierPreviewLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', supplierFile);
      const response = await axios.post(`${API}/import/preview-supplier-headers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSupplierMapperHeaders(response.data.headers);
      setSupplierMapperSamples(response.data.sample_rows);
      setSupplierMapperOpen(true);
    } catch (err) {
      setSupplierResult({ error: true, message: err.response?.data?.detail || 'Failed to read file headers' });
    } finally {
      setSupplierPreviewLoading(false);
    }
  };

  // Handle supplier field mapping confirmation
  const handleSupplierMappingConfirm = (mapping) => {
    setSupplierMapperOpen(false);
    setSupplierFieldMapping(mapping);
    previewSuppliers(mapping);
  };

  // Import suppliers
  const importSuppliers = async () => {
    if (!supplierPreview || !currentOrg) return;
    setSupplierImporting(true);
    setSupplierPreviewOpen(false);
    try {
      const formData = new FormData();
      formData.append('organization_id', currentOrg.id);
      formData.append('records', JSON.stringify(supplierPreview.all_records));
      const response = await axios.post(`${API}/import/suppliers`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000
      });
      setSupplierResult(response.data);
      setSupplierPreview(null);
    } catch (err) {
      setSupplierResult({ error: true, message: err.response?.data?.detail || 'Import failed' });
    } finally {
      setSupplierImporting(false);
    }
  };

  const getSystemFields = () => {
    if (mapperType === 'coa') return COA_FIELDS;
    if (mapperType === 'voucher') return VOUCHER_FIELDS;
    if (mapperType === 'inventory') return INVENTORY_FIELDS;
    return [];
  };

  const formatBalance = (balance, currency) => {
    if (balance === 0) return '-';
    const formatted = Math.abs(balance).toLocaleString();
    const prefix = balance < 0 ? '-' : '';
    return `${prefix}${formatted} ${currency}`;
  };

  if (!currentOrg) {
    return <div className="flex items-center justify-center h-64"><p className="text-muted-foreground">Please select an organization</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="import-data-page">
      <div>
        <h1 className="text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Import Data</h1>
        <p className="text-muted-foreground text-xs lg:text-sm mt-1">
          Import from Excel for <strong>{currentOrg?.name}</strong>
        </p>
      </div>

      {/* Customer & Supplier Import - NEW SECTION */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded text-xs">NEW</span>
          Customer & Supplier Import
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Customer Import */}
          <Card className="border-indigo-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-indigo-500" />
                Customers
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Creates accounts under <code className="bg-gray-100 px-1 rounded">4111</code>. 
                Auto-generates opening balance vouchers vs Capital (<code className="bg-gray-100 px-1 rounded">10110001</code>).
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <FileUploader file={customerFile} setFile={setCustomerFile} onClear={() => { setCustomerResult(null); setCustomerPreview(null); setCustomerFieldMapping(null); }} />
              <div className="flex gap-2">
                <Button 
                  onClick={() => previewCustomers()} 
                  disabled={!customerFile || customerPreviewLoading || customerImporting} 
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700" 
                  size="sm"
                >
                  {customerPreviewLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
                  {customerPreviewLoading ? 'Reading...' : customerImporting ? 'Importing...' : 'Preview'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={openCustomerFieldMapper} 
                  disabled={!customerFile || customerPreviewLoading} 
                  size="sm"
                >
                  <Columns className="w-3 h-3 mr-1" />Match Fields
                </Button>
              </div>
              <ResultDisplay result={customerResult} />
            </CardContent>
          </Card>

          {/* Supplier Import */}
          <Card className="border-orange-200">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Truck className="w-4 h-4 text-orange-500" />
                Suppliers
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Creates accounts under <code className="bg-gray-100 px-1 rounded">4011</code>. 
                Auto-generates opening balance vouchers vs Capital (<code className="bg-gray-100 px-1 rounded">10110001</code>).
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <FileUploader file={supplierFile} setFile={setSupplierFile} onClear={() => { setSupplierResult(null); setSupplierPreview(null); setSupplierFieldMapping(null); }} />
              <div className="flex gap-2">
                <Button 
                  onClick={() => previewSuppliers()} 
                  disabled={!supplierFile || supplierPreviewLoading || supplierImporting} 
                  className="flex-1 bg-orange-600 hover:bg-orange-700" 
                  size="sm"
                >
                  {supplierPreviewLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Eye className="w-3 h-3 mr-1" />}
                  {supplierPreviewLoading ? 'Reading...' : supplierImporting ? 'Importing...' : 'Preview'}
                </Button>
                <Button 
                  variant="outline"
                  onClick={openSupplierFieldMapper} 
                  disabled={!supplierFile || supplierPreviewLoading} 
                  size="sm"
                >
                  <Columns className="w-3 h-3 mr-1" />Match Fields
                </Button>
              </div>
              <ResultDisplay result={supplierResult} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Step 1 */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Legacy Import — Reference Data</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm"><FolderOpen className="w-4 h-4 text-purple-500" />Categories</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <FileUploader file={catFile} setFile={setCatFile} onClear={() => setCatResult(null)} />
              <Button onClick={() => doSimpleImport('/import/categories', catFile, setCatImporting, setCatResult)} disabled={!catFile || catImporting} className="w-full" size="sm">
                {catImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{catImporting ? 'Importing...' : 'Import Categories'}
              </Button>
              <ResultDisplay result={catResult} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-teal-500" />Regions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <FileUploader file={regionFile} setFile={setRegionFile} onClear={() => setRegionResult(null)} />
              <Button onClick={() => doSimpleImport('/import/regions', regionFile, setRegionImporting, setRegionResult)} disabled={!regionFile || regionImporting} className="w-full" size="sm">
                {regionImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{regionImporting ? 'Importing...' : 'Import Regions'}
              </Button>
              <ResultDisplay result={regionResult} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Step 2 */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Legacy Import — Chart of Accounts</h2>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><BookOpen className="w-4 h-4 text-blue-500" />Chart of Accounts</CardTitle>
            <p className="text-xs text-muted-foreground">Auto-detects suppliers (40*), customers (41* + region), registration numbers</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <FileUploader file={coaFile} setFile={setCoaFile} onClear={() => setCoaResult(null)} />
            <div className="flex gap-2">
              <Button onClick={() => doSimpleImport('/import/chart-of-accounts', coaFile, setCoaImporting, setCoaResult)} disabled={!coaFile || coaImporting} className="flex-1" size="sm">
                {coaImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{coaImporting ? 'Importing...' : 'Quick Import (Default Mapping)'}
              </Button>
              <Button variant="outline" onClick={() => startMapping('coa', coaFile)} disabled={!coaFile || coaImporting || previewLoading} size="sm">
                <Columns className="w-3 h-3 mr-1" />Match Fields
              </Button>
            </div>
            <ResultDisplay result={coaResult} />
          </CardContent>
        </Card>
      </div>

      {/* Step 3 */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Legacy Import — Inventory Items</h2>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Package className="w-4 h-4 text-amber-500" />Inventory Items</CardTitle>
            <p className="text-xs text-muted-foreground">Links to category (CAT_ID) and supplier (SUP_ID). No barcode, no qty on hand.</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <FileUploader file={itemsFile} setFile={setItemsFile} onClear={() => setItemsResult(null)} />
            <div className="flex gap-2">
              <Button onClick={() => doSimpleImport('/import/inventory', itemsFile, setItemsImporting, setItemsResult)} disabled={!itemsFile || itemsImporting} className="flex-1 bg-amber-600 hover:bg-amber-700" size="sm">
                {itemsImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{itemsImporting ? 'Importing...' : 'Quick Import (Default Mapping)'}
              </Button>
              <Button variant="outline" onClick={() => startMapping('inventory', itemsFile)} disabled={!itemsFile || itemsImporting || previewLoading} size="sm">
                <Columns className="w-3 h-3 mr-1" />Match Fields
              </Button>
            </div>
            <ResultDisplay result={itemsResult} />
          </CardContent>
        </Card>
      </div>

      {/* Step 4 */}
      <div>
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Legacy Import — Voucher History</h2>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm"><Receipt className="w-4 h-4 text-emerald-500" />Voucher History</CardTitle>
            <p className="text-xs text-muted-foreground">Auto-posted. Skips vouchers outside selected FY. Rate: 89,500 LBP (2023+) / 1,507.5 LBP (before 2023)</p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs font-medium">Fiscal Year (required — skips outside FY)</Label>
              <Select value={voucherFYId} onValueChange={setVoucherFYId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select fiscal year..." /></SelectTrigger>
                <SelectContent>
                  {fiscalYears.map(fy => (
                    <SelectItem key={fy.id} value={fy.id}>{fy.name} ({fy.start_date} to {fy.end_date})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <FileUploader file={voucherFile} setFile={setVoucherFile} onClear={() => setVoucherResult(null)} />
            <div className="flex gap-2">
              <Button onClick={() => doSimpleImport('/import/vouchers', voucherFile, setVoucherImporting, setVoucherResult, { fiscal_year_id: voucherFYId })} 
                disabled={!voucherFile || voucherImporting || !voucherFYId} className="flex-1 bg-emerald-600 hover:bg-emerald-700" size="sm">
                {voucherImporting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}{voucherImporting ? 'Importing...' : 'Quick Import (Default Mapping)'}
              </Button>
              <Button variant="outline" onClick={() => startMapping('voucher', voucherFile)} disabled={!voucherFile || voucherImporting || !voucherFYId || previewLoading} size="sm">
                <Columns className="w-3 h-3 mr-1" />Match Fields
              </Button>
            </div>
            <ResultDisplay result={voucherResult} />
          </CardContent>
        </Card>
      </div>

      {/* Field Mapping Dialog */}
      <Dialog open={mapperOpen} onOpenChange={setMapperOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5 text-blue-500" />
              Field Mapping — {mapperType === 'coa' ? 'Chart of Accounts' : mapperType === 'voucher' ? 'Vouchers' : 'Inventory'}
            </DialogTitle>
            <DialogDescription>
              Match each system field to the correct Excel column. Sample data shown for reference.
            </DialogDescription>
          </DialogHeader>
          {mapperHeaders.length > 0 && (
            <FieldMapper
              headers={mapperHeaders}
              sampleRows={mapperSamples}
              systemFields={getSystemFields()}
              onConfirm={doMappedImport}
              onCancel={() => setMapperOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Preview Dialog */}
      <Dialog open={customerPreviewOpen} onOpenChange={setCustomerPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Customer Import Preview
            </DialogTitle>
            <DialogDescription>
              Review the data before importing. Existing customers will be updated; new ones will be created.
            </DialogDescription>
          </DialogHeader>
          
          {customerPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold">{customerPreview.total_records}</div>
                  <div className="text-xs text-muted-foreground">Total Records</div>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-600">{customerPreview.new_count}</div>
                  <div className="text-xs text-muted-foreground">New Customers</div>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-amber-600">{customerPreview.existing_count}</div>
                  <div className="text-xs text-muted-foreground">Updates</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{customerPreview.with_balance}</div>
                  <div className="text-xs text-muted-foreground">With Balance</div>
                </div>
              </div>

              {/* Preview Table */}
              <ScrollArea className="h-[300px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead className="w-[80px]">Currency</TableHead>
                      <TableHead className="w-[120px] text-right">Balance</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {customerPreview.preview.map((record, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{record.name}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{record.address}</TableCell>
                        <TableCell>
                          <Badge variant={record.currency === 'USD' ? 'default' : 'secondary'} className="text-xs">
                            {record.currency}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-mono ${record.balance < 0 ? 'text-red-600' : record.balance > 0 ? 'text-emerald-600' : ''}`}>
                          {formatBalance(record.balance, record.currency)}
                        </TableCell>
                        <TableCell>
                          {record.exists ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Update</Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-xs">New</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              {customerPreview.total_records > 20 && (
                <p className="text-xs text-muted-foreground text-center">
                  Showing first 20 of {customerPreview.total_records} records
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomerPreviewOpen(false)}>Cancel</Button>
            <Button 
              onClick={importCustomers} 
              disabled={customerImporting}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {customerImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import {customerPreview?.total_records} Customers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Preview Dialog */}
      <Dialog open={supplierPreviewOpen} onOpenChange={setSupplierPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              Supplier Import Preview
            </DialogTitle>
            <DialogDescription>
              Review the data before importing. Existing suppliers will be updated; new ones will be created.
            </DialogDescription>
          </DialogHeader>
          
          {supplierPreview && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold">{supplierPreview.total_records}</div>
                  <div className="text-xs text-muted-foreground">Total Records</div>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-emerald-600">{supplierPreview.new_count}</div>
                  <div className="text-xs text-muted-foreground">New Suppliers</div>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-amber-600">{supplierPreview.existing_count}</div>
                  <div className="text-xs text-muted-foreground">Updates</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{supplierPreview.with_balance}</div>
                  <div className="text-xs text-muted-foreground">With Balance</div>
                </div>
              </div>

              {/* Preview Table */}
              <ScrollArea className="h-[300px] border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[200px]">Name</TableHead>
                      <TableHead>Address</TableHead>
                      <TableHead className="w-[80px]">Currency</TableHead>
                      <TableHead className="w-[120px] text-right">Balance</TableHead>
                      <TableHead className="w-[80px]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {supplierPreview.preview.map((record, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{record.name}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">{record.address}</TableCell>
                        <TableCell>
                          <Badge variant={record.currency === 'USD' ? 'default' : 'secondary'} className="text-xs">
                            {record.currency}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-mono ${record.balance < 0 ? 'text-red-600' : record.balance > 0 ? 'text-emerald-600' : ''}`}>
                          {formatBalance(record.balance, record.currency)}
                        </TableCell>
                        <TableCell>
                          {record.exists ? (
                            <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">Update</Badge>
                          ) : (
                            <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-xs">New</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
              {supplierPreview.total_records > 20 && (
                <p className="text-xs text-muted-foreground text-center">
                  Showing first 20 of {supplierPreview.total_records} records
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierPreviewOpen(false)}>Cancel</Button>
            <Button 
              onClick={importSuppliers} 
              disabled={supplierImporting}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {supplierImporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Import {supplierPreview?.total_records} Suppliers
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Field Mapping Dialog */}
      <Dialog open={customerMapperOpen} onOpenChange={setCustomerMapperOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5 text-indigo-500" />
              Field Mapping — Customers
            </DialogTitle>
            <DialogDescription>
              Match each system field to the correct Excel column. Sample data shown for reference.
            </DialogDescription>
          </DialogHeader>
          {customerMapperHeaders.length > 0 && (
            <FieldMapper
              headers={customerMapperHeaders}
              sampleRows={customerMapperSamples}
              systemFields={CUSTOMER_SUPPLIER_FIELDS}
              onConfirm={handleCustomerMappingConfirm}
              onCancel={() => setCustomerMapperOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Supplier Field Mapping Dialog */}
      <Dialog open={supplierMapperOpen} onOpenChange={setSupplierMapperOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5 text-orange-500" />
              Field Mapping — Suppliers
            </DialogTitle>
            <DialogDescription>
              Match each system field to the correct Excel column. Sample data shown for reference.
            </DialogDescription>
          </DialogHeader>
          {supplierMapperHeaders.length > 0 && (
            <FieldMapper
              headers={supplierMapperHeaders}
              sampleRows={supplierMapperSamples}
              systemFields={CUSTOMER_SUPPLIER_FIELDS}
              onConfirm={handleSupplierMappingConfirm}
              onCancel={() => setSupplierMapperOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportDataPage;
